/**
 * useProjectStore — Project CRUD (PARA method).
 *
 * Storage: 'projects' (collection)
 * Entity: { id, name, description, color, emoji, status, areaId, phases[], properties{}, ... }
 * Methods: addProject, updateProject, deleteProject, updateProjectStatus, archiveProject
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';
import { getPlainText } from '../components/ui/DualModeEditor';

/** Coerce a description (TipTap JSON object/string or plain string) to a plain string. */
function normalizeDescription(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    return getPlainText(value);
}

const STORAGE_KEY = 'projects';

const useProjectStore = create((set, get) => ({
    projects: storage.get(STORAGE_KEY, []),

    _persist() {
        storage.set(STORAGE_KEY, get().projects);
    },

    addProject(project) {
        const defaultPhase = { id: crypto.randomUUID(), name: 'Phase 1', order: 0 };
        const newProject = {
            id: crypto.randomUUID(),
            name: project.name,
            description: normalizeDescription(project.description),
            color: project.color || 'categorical-2',
            emoji: project.emoji || '📁',
            coverImage: project.coverImage || null,
            status: 'planned', // 'planned' | 'active' | 'completed' | 'archived'
            areaId: project.areaId || null,
            dueDate: project.dueDate || null,
            startDate: project.startDate || null,
            phases: project.phases || [defaultPhase],
            isKickedOff: project.isKickedOff || false,
            pinnedTaskIds: [],
            properties: project.properties || {},
            createdAt: new Date().toISOString(),
        };
        set(state => {
            const updated = [...state.projects, newProject];
            storage.set(STORAGE_KEY, updated);
            return { projects: updated };
        });
        return newProject;
    },

    updateProject(id, updates) {
        const safeUpdates = 'description' in updates
            ? { ...updates, description: normalizeDescription(updates.description) }
            : updates;
        set(state => {
            const updated = state.projects.map(p =>
                p.id === id ? { ...p, ...safeUpdates } : p
            );
            storage.set(STORAGE_KEY, updated);
            return { projects: updated };
        });
    },

    deleteProject(id) {
        set(state => {
            const updated = state.projects.filter(p => p.id !== id);
            storage.set(STORAGE_KEY, updated);
            return { projects: updated };
        });
    },

}));

// Re-read from localStorage after cloud sync completes
onRehydrate(() => {
    useProjectStore.setState({ projects: storage.get(STORAGE_KEY, []) });
});

export default useProjectStore;

